import { useEffect, useState } from 'react';
import { Car, Filter, Download, FileText } from 'lucide-react';
import { getMyBookings, cancelBooking, downloadReceipt } from '../../services/api';
import toast from 'react-hot-toast';

const StatusBadge = ({ status }) => <span className={`badge-${status}`}>{status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>;

const STATUSES = ['all', 'pending', 'confirmed', 'assigned', 'in_progress', 'completed', 'cancelled'];

export default function MyRides() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [cancelling, setCancelling] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const params = status !== 'all' ? { status } : {};
      const res = await getMyBookings({ ...params, limit: 50 });
      setBookings(res.data.bookings);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); }, [filter]);

  const handleCancel = async (uuid) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancelling(uuid);
    try {
      await cancelBooking(uuid, { cancellation_reason: 'Cancelled by user' });
      toast.success('Booking cancelled.');
      setBookings(b => b.map(x => x.uuid === uuid ? { ...x, status: 'cancelled' } : x));
    } catch {
      toast.error('Could not cancel booking.');
    } finally {
      setCancelling(null);
    }
  };

  const handleDownloadReceipt = async (b) => {
    setDownloading(b.uuid);
    try {
      const res = await downloadReceipt(b.uuid);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MDS-Receipt-${b.uuid.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded!');
    } catch {
      toast.error('Could not download receipt. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading">My Rides</h1>
          <p className="text-sm text-gray-500">All your transportation bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-gray-400" />
          <div className="flex flex-wrap gap-1">
            {STATUSES.map(s => (
              <button key={s} onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${filter === s ? 'bg-purple-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-700" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="card border border-gray-100 text-center py-16 text-gray-400">
          <Car size={48} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold text-gray-600">No rides found</p>
          <p className="text-sm mt-1">Try a different filter or book a new ride</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map(b => (
            <div key={b.uuid} className="card border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Status + Type row */}
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <StatusBadge status={b.status} />
                    <span className="text-xs text-gray-400 capitalize">
                      {b.service_type?.replace(/_/g, ' ')} · {b.ride_type?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-gray-300">#{b.uuid.slice(0, 8).toUpperCase()}</span>
                  </div>

                  {/* Route */}
                  <h3 className="font-semibold text-gray-900">
                    {b.pickup_address?.slice(0, 45)}{b.pickup_address?.length > 45 ? '…' : ''} → {b.destination_address?.slice(0, 45)}{b.destination_address?.length > 45 ? '…' : ''}
                  </h3>

                  {/* Date / time */}
                  <p className="text-sm text-gray-500 mt-1">
                    📅 {b.appointment_date} at {b.appointment_time?.slice(0, 5)}
                    {b.pickup_city && ` · Pickup: ${b.pickup_city}`}
                  </p>

                  {/* Mobility */}
                  {(b.needs_wheelchair || b.needs_walker) && (
                    <p className="text-xs text-purple-600 mt-1">
                      {b.needs_wheelchair && '♿ Wheelchair '}
                      {b.needs_walker && '🦯 Walker'}
                    </p>
                  )}

                  {/* Driver info */}
                  {b.driver_first && (
                    <p className="text-sm text-gray-600 mt-1">
                      🚗 Driver: <span className="font-medium">{b.driver_first} {b.driver_last}</span>
                      {b.driver_phone && ` · ${b.driver_phone}`}
                    </p>
                  )}

                  {/* Special instructions */}
                  {b.special_instructions && (
                    <p className="text-xs text-gray-500 mt-1 italic">📝 {b.special_instructions}</p>
                  )}

                  {/* Completed notice */}
                  {b.status === 'completed' && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                      <FileText size={11} />
                      Receipt available — check your email or download below
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {/* Download receipt — available for completed bookings */}
                  {b.status === 'completed' && (
                    <button
                      onClick={() => handleDownloadReceipt(b)}
                      disabled={downloading === b.uuid}
                      className="flex items-center gap-1.5 text-xs text-purple-700 border border-purple-200 hover:bg-purple-50 hover:border-purple-400 px-3 py-1.5 rounded-lg transition-colors font-medium"
                    >
                      {downloading === b.uuid
                        ? <div className="w-3 h-3 border border-purple-600/40 border-t-purple-600 rounded-full animate-spin" />
                        : <Download size={13} />
                      }
                      {downloading === b.uuid ? 'Downloading…' : 'Download Receipt'}
                    </button>
                  )}

                  {/* Cancel button */}
                  {['pending', 'confirmed'].includes(b.status) && (
                    <button
                      onClick={() => handleCancel(b.uuid)}
                      disabled={cancelling === b.uuid}
                      className="text-xs text-red-600 hover:text-red-800 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {cancelling === b.uuid ? 'Cancelling…' : 'Cancel Ride'}
                    </button>
                  )}

                  <span className="text-xs text-gray-400">{new Date(b.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
